import { autoUpdate, flip, offset, shift } from '@floating-ui/react-dom'
import { FloatingPortal, useDismiss, useFloating, useInteractions } from '@floating-ui/react-dom-interactions'
import { CreateInlineMenu } from 'components/CreateInlineMenu/CreateInlineMenu'
import { NewInlineOption } from 'components/CreateInlineMenu/types'
import { handleKeydown } from 'components/CreateInlineMenu/utils'
import { useIsBlaze } from 'hooks/useIsBlaze'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import useNewEntrypointModal from 'hooks/useNewEntrypointModal'
import { noop } from 'lodash'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import Styles from './NewFolderInlineMenu.module.scss'
import { useCreateFolder } from './useCreateFolder'
import { BoardView, FolderClosed, TableView } from 'almanac-ui/icons'
import { useDocumentationFeatures } from 'hooks/useDocumentationFeatures'
import { AlmanacFile } from 'types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  doNotRedirect?: boolean
  onClose: () => void
  setShowCreateMenu: (show: boolean) => void
  options?: { pinned?: boolean; shared?: boolean }
  listItems?: NewInlineOption[]
  onSuccess?: (folder: AlmanacFile) => void
  workspaceKey?: string
}

export const NewFolderInlineMenu = ({
  onClose,
  setShowCreateMenu,
  listItems,
  doNotRedirect,
  options,
  onSuccess,
  workspaceKey,
}: Props) => {
  const [query, setQuery] = useState('')
  const isBlaze = useIsBlaze()
  const [chosenOption, setChosenOption] = useState<NewInlineOption | string>(
    isBlaze ? NewInlineOption.Database : NewInlineOption.Handbook,
  )
  const { openNewEntrypointModal } = useNewEntrypointModal()
  const { isDocumentationEnabled } = useDocumentationFeatures()
  const workspace = useCurrentWorkspace()
  const currentWorkspaceKey = workspaceKey || workspace?.key
  const { isMobile } = useResponsiveDesign()

  const defaultListItems =
    listItems ||
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line no-nested-ternary
    (isBlaze
      ? isDocumentationEnabled
        ? [NewInlineOption.Database, NewInlineOption.Handbook]
        : []
      : [NewInlineOption.Handbook, NewInlineOption.BoardView, NewInlineOption.More])

  const { createFolder } = useCreateFolder({
    onClose,
    chosenOption,
    query,
    setShowCreateMenu,
    doNotRedirect,
    options,
    onSuccess,
    workspaceKey: currentWorkspaceKey,
  })

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 0)
  }, [])

  const inputRef = useRef<HTMLInputElement | null>(null)

  const openCreateNewModal = () => {
    openNewEntrypointModal()
    onClose()
  }

  const handleInputKeydown = (e: React.KeyboardEvent) => {
    const shiftKey = e.shiftKey

    handleKeydown({
      event: e,
      listItems: defaultListItems,
      chosenOption,
      setChosenOption,
      handleDocClick: noop,
      handleFolderClick: () => {
        createFolder(shiftKey)
      },
      handleMoreClick: openCreateNewModal,
      handleSectionClick: noop,
      handleLinkClick: noop,
      handleSuggestedDocClick: noop,
      reset: onClose,
    })
  }

  const { x, y, reference, floating, context, refs } = useFloating({
    open: true,
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [offset(4), shift({ padding: 4 }), flip()],
    whileElementsMounted: (reference, floating, update) => {
      return autoUpdate(reference, floating, update, {
        animationFrame: true,
      })
    },
  })
  const dismiss = useDismiss(context, {
    bubbles: true,
    outsidePressEvent: 'click',
    outsidePress: (event) => {
      const prompt = refs.floating.current
      const input = inputRef.current
      const target = event.target as HTMLElement
      if (prompt !== target && !prompt?.contains(target) && !input?.contains(target)) {
        onClose()
      }
      return false
    },
  })
  const { getFloatingProps } = useInteractions([dismiss])
  const inputHasValue = (e: React.FocusEvent<HTMLInputElement, Element>) => e.target.value.trim().length > 0

  return (
    <div className={Styles.inputWrapper} ref={reference} data-testid="new-folder-inline-menu">
      <div className={Styles.newItemMenu}>
        <div className={Styles.icon}>
          {chosenOption === NewInlineOption.BoardView && <BoardView />}
          {chosenOption === NewInlineOption.Database && <TableView />}
          {chosenOption === NewInlineOption.Handbook && <FolderClosed />}
        </div>
        <input
          ref={inputRef}
          placeholder={'Create...'}
          onKeyDown={handleInputKeydown}
          onChange={(e) => {
            setQuery(e.target.value)
          }}
          onBlur={(e) => {
            const prompt = refs.floating.current
            const target = e.relatedTarget as Node
            if (prompt !== target && !prompt?.contains(target)) {
              if (isMobile && inputHasValue(e)) createFolder(false)
              onClose()
            }
          }}
        />
      </div>
      <FloatingPortal id="floating-ui-root">
        <div ref={floating} style={{ position: 'fixed', top: y || 0, left: x || 0 }} {...getFloatingProps()}>
          <CreateInlineMenu
            close={onClose}
            onCreateDoc={noop}
            onCreateFolder={() => {
              createFolder(false)
            }}
            listItems={defaultListItems}
            isProcessing={false} // TODO
            onCreateSuggestedDoc={noop}
            onOpenMore={openCreateNewModal}
            dropdownStyle={Styles.paddingTop}
            inputValue={query}
            chosenOption={chosenOption}
            setChosenOption={setChosenOption}
            updateListItems={noop}
            suggestedDocs={[]}
            setSuggestedDocs={noop}
            showPasteText={false}
            showSuggestions={false}
          />
        </div>
      </FloatingPortal>
    </div>
  )
}
